import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import StudentHeader from "../../components/Tutor/StudentDetails/StudentHeader";
import UpdatesCard from "../../components/Tutor/UpdatesCard";
import AssignmentCardsRow from "../../components/Student/AssignmentCardsRow";
import AnalyticsHeader from "../../components/Student/Analytics/AnalyticsHeader";
import SectionView from "../../components/Student/Analytics/SectionView";

import { getStudentProfile } from "../../utils/tutor/getStudentProfile";
import { getStudentAssignments } from "../../utils/tutor/getStudentAssignments";
import { getStudentDiagnosticAttempts } from "../../utils/tutor/getStudentDiagnosticAttempts";
import { convertDiagnosticToAssignment } from "../../utils/tutor/convertDiagnosticToAssignment";

const formatRelativeTime = (date) => {
  if (!date) return "—";

  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const StudentDetailPage = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();

  const [student, setStudent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [diagnosticAttempts, setDiagnosticAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("english");

  useEffect(() => {
    if (!studentId) return;

    const load = async () => {
      try {
        const [profile, assignmentData, diagAttempts] = await Promise.all([
          getStudentProfile(studentId),
          getStudentAssignments(studentId),
          getStudentDiagnosticAttempts(studentId),
        ]);

        setStudent(profile);
        setAssignments(Array.isArray(assignmentData) ? assignmentData : []);
        setDiagnosticAttempts(Array.isArray(diagAttempts) ? diagAttempts : []);
      } catch (err) {
        console.error("Failed to load student detail", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [studentId]);

  const diagnosticAsAssignments = useMemo(() => {
    return (diagnosticAttempts || []).map((attempt) =>
      convertDiagnosticToAssignment(attempt.id, attempt)
    );
  }, [diagnosticAttempts]);

  const mergedAssignments = useMemo(() => {
    const all = [...(assignments || []), ...(diagnosticAsAssignments || [])];

    const getSortTime = (item) => {
      const d = item?.lastSavedAt?.toDate?.();
      if (d instanceof Date) return d;

      const a = item?.assignedAt?.toDate?.();
      if (a instanceof Date) return a;

      return null;
    };

    return all
      .slice()
      .sort((a, b) => {
        const ta = getSortTime(a)?.getTime?.() ?? 0;
        const tb = getSortTime(b)?.getTime?.() ?? 0;
        return tb - ta;
      });
  }, [assignments, diagnosticAsAssignments]);

  // ✅ FIX: define the handler you pass down
  const handleViewItem = (a) => {
    if (!a) return;

    if (String(a?.type ?? "").toLowerCase() === "diagnostic") {
      const attemptId = a.diagnosticAttemptId || a.diagnosticId || a.id;

      navigate(`/tutor/diagnostics/${studentId}/${attemptId}/review`, {
        state: { readOnly: true },
      });
      return;
    }

    // Regular assignment review (existing)
    // navigate(`/tutor/assignments/${a.id}/question`, {
    //   state: { assignment: a, readOnly: true },
    // });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!student) {
    return <div>Student not found.</div>;
  }

  const analytics = student.analytics ?? {};
  const pillarEstimates = student.pillarEstimates ?? {};
  const estimateHistory = student.estimateHistory ?? {};

  const updates = [
    ...(student.createdAt?.toDate
      ? [
          {
            student: student.firstName,
            action: "joined Office Hours",
            time: formatRelativeTime(student.createdAt.toDate()),
            score: "—",
            sortTime: student.createdAt.toDate(),
          },
        ]
      : []),

    ...diagnosticAttempts
      .map((d) => {
        const sortTime =
          d.completedAt?.toDate?.() ??
          d.lastSavedAt?.toDate?.() ??
          d.createdAt?.toDate?.();

        if (!sortTime) return null;

        const section =
          d.section ??
          (String(d.diagnosticId || d.id || "").toLowerCase().includes("math")
            ? "Math"
            : String(d.diagnosticId || d.id || "").toLowerCase().includes("english")
            ? "English"
            : "Diagnostic");

        const action =
          d.status === "completed"
            ? `completed SAT ${section} Diagnostic`
            : d.status === "in_progress"
            ? `started SAT ${section} Diagnostic`
            : `assigned SAT ${section} Diagnostic`;

        const responses = d.responses || {};
        const entries = Object.values(responses);
        const correct = entries.reduce((sum, r) => sum + (r?.correct ? 1 : 0), 0);
        const total =
          d.administeredCount ??
          (Array.isArray(d.sequence) ? d.sequence.length : entries.length);

        const score = d.status === "completed" ? `${correct} / ${total}` : "—";

        return {
          student: student.firstName,
          action,
          time: formatRelativeTime(sortTime),
          score,
          sortTime,
        };
      })
      .filter(Boolean),

    ...assignments.map((a) => {
      const eventTime = a.lastSavedAt?.toDate?.() ?? a.assignedAt?.toDate?.();

      return {
        student: student.firstName,
        action:
          a.status === "Complete"
            ? `completed ${a.title}`
            : a.status === "In Progress"
            ? `started ${a.title}`
            : `assigned ${a.title}`,
        time: formatRelativeTime(eventTime),
        score:
          a.status === "Complete"
            ? `${a.correct} / ${a.correct + a.incorrect}`
            : "—",
        sortTime: eventTime,
      };
    }),
  ]
    .filter((u) => u.sortTime)
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, 5)
    .map(({ sortTime, ...rest }) => rest);

  const lastActiveLabel = updates.length > 0 ? updates[0].time : "—";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StudentHeader
        student={{
          name: `${student.firstName} ${student.lastName}`,
          email: student.email,
          lastActive: lastActiveLabel,
        }}
      />

      <UpdatesCard updates={updates} />

      <AssignmentCardsRow
        assignments={mergedAssignments}
        hideCompletedDiagnostics={false}
        onView={handleViewItem}
      />

      <AnalyticsHeader
        activeView={activeView}
        onChange={setActiveView}
        studentName={student.firstName}
      />

      {activeView === "english" && (
        <SectionView
          section="English"
          analytics={analytics?.english?.skills ?? {}}
          pillarEstimates={pillarEstimates.english ?? {}}
          estimateHistory={estimateHistory}
        />
      )}

      {activeView === "math" && (
        <SectionView
          section="Math"
          analytics={analytics?.math?.skills ?? {}}
          pillarEstimates={pillarEstimates.math ?? {}}
          estimateHistory={estimateHistory}
        />
      )}
    </Box>
  );
};

export default StudentDetailPage;
